import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Field, SaveBtn, ImageField } from "@/pages/admin/AdminCatalog";

export const AdminJournal = () => {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const load = () => api.get("/journal").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    const body = { title: edit.title, slug: edit.slug, date: edit.date, excerpt: edit.excerpt, content: edit.content, image: edit.image || "", active: edit.active ?? true };
    try {
      if (isNew) await api.post("/admin/journal", body);
      else await api.put(`/admin/journal/${edit.slug}`, body);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (slug) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/journal/${slug}`); toast.success("Deleted"); load();
  };
  if (edit) return (
    <div className="max-w-2xl">
      <Field label="Title" value={edit.title} onChange={(v) => setEdit({ ...edit, title: v })} />
      <Field label="Slug" value={edit.slug} onChange={(v) => setEdit({ ...edit, slug: v })} />
      <Field label="Date label" value={edit.date} onChange={(v) => setEdit({ ...edit, date: v })} />
      <ImageField label="Image" value={edit.image} onChange={(v) => setEdit({ ...edit, image: v })} />
      <Field label="Excerpt" rows={2} value={edit.excerpt} onChange={(v) => setEdit({ ...edit, excerpt: v })} />
      <Field label="Content (blank line = new paragraph)" rows={12} value={edit.content} onChange={(v) => setEdit({ ...edit, content: v })} />
      <div className="flex gap-3">
        <SaveBtn onClick={save} testid="journal-save-btn" />
        <button data-testid="journal-cancel-btn" onClick={() => setEdit(null)} className="border border-[#2B2118]/20 px-6 py-2.5 text-xs tracking-[0.2em] uppercase text-[#5A4C3C]">Cancel</button>
      </div>
    </div>
  );
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display text-2xl">Journal</h2>
        <SaveBtn onClick={() => { setIsNew(true); setEdit({ title: "", slug: "", date: "", excerpt: "", content: "", image: "" }); }} testid="journal-add-btn">Add post</SaveBtn>
      </div>
      <div className="divide-y divide-[#2B2118]/10 border border-[#2B2118]/10">
        {list.map((p) => (
          <div key={p.slug} className="flex items-center gap-4 p-4">
            <div className="flex-1"><p className="text-sm">{p.title}</p><p className="text-xs text-[#6E6151]">{p.date}</p></div>
            <button data-testid={`journal-edit-${p.slug}`} onClick={() => { setIsNew(false); setEdit(p); }} className="text-xs text-[#8F6B3A] hover:underline">Edit</button>
            <button data-testid={`journal-delete-${p.slug}`} onClick={() => remove(p.slug)} className="text-xs text-red-400/70">Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AdminFaq = () => {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/faq").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    const body = { question: edit.question, answer: edit.answer, order: edit.order || 0 };
    if (edit.id) await api.put(`/admin/faq/${edit.id}`, body);
    else await api.post("/admin/faq", body);
    toast.success("Saved"); setEdit(null); load();
  };
  const remove = async (id) => { await api.delete(`/admin/faq/${id}`); toast.success("Deleted"); load(); };
  if (edit) return (
    <div className="max-w-2xl">
      <Field label="Question" value={edit.question} onChange={(v) => setEdit({ ...edit, question: v })} />
      <Field label="Answer" rows={4} value={edit.answer} onChange={(v) => setEdit({ ...edit, answer: v })} />
      <Field label="Order" type="number" value={edit.order} onChange={(v) => setEdit({ ...edit, order: v })} />
      <div className="flex gap-3">
        <SaveBtn onClick={save} testid="faq-save-btn" />
        <button onClick={() => setEdit(null)} className="border border-[#2B2118]/20 px-6 py-2.5 text-xs tracking-[0.2em] uppercase text-[#5A4C3C]">Cancel</button>
      </div>
    </div>
  );
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display text-2xl">FAQ</h2>
        <SaveBtn onClick={() => setEdit({ question: "", answer: "", order: list.length })} testid="faq-add-btn">Add FAQ</SaveBtn>
      </div>
      <div className="divide-y divide-[#2B2118]/10 border border-[#2B2118]/10">
        {list.map((f) => (
          <div key={f.id} className="flex items-center gap-4 p-4">
            <p className="flex-1 text-sm">{f.question}</p>
            <button data-testid={`faq-edit-${f.id}`} onClick={() => setEdit(f)} className="text-xs text-[#8F6B3A] hover:underline">Edit</button>
            <button onClick={() => remove(f.id)} className="text-xs text-red-400/70">Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
};

const POLICY_KEYS = ["shipping-delivery", "returns-exchanges", "privacy", "terms", "complaints"];

export const AdminPolicies = () => {
  const [key, setKey] = useState(POLICY_KEYS[0]);
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/policies/${key}`).then((r) => setP(r.data)).catch(() => setP({ title: "", updated: "", note: "", sections: [] })); }, [key]);
  const save = async () => { await api.put(`/admin/policies/${key}`, { data: p }); toast.success("Saved"); };
  if (!p) return null;
  const setSection = (i, field, v) => {
    const sections = [...p.sections]; sections[i] = { ...sections[i], [field]: v }; setP({ ...p, sections });
  };
  return (
    <div className="max-w-3xl">
      <div className="flex flex-wrap gap-2 mb-8">
        {POLICY_KEYS.map((k) => (
          <button key={k} data-testid={`policy-select-${k}`} onClick={() => setKey(k)}
            className={`px-3 py-1.5 text-xs border ${key === k ? "border-[#8F6B3A] text-[#8F6B3A]" : "border-[#2B2118]/10 text-[#6E6151]"}`}>{k}</button>
        ))}
      </div>
      <Field label="Title" value={p.title} onChange={(v) => setP({ ...p, title: v })} />
      <Field label="Last updated label" value={p.updated} onChange={(v) => setP({ ...p, updated: v })} />
      <Field label="Visible note (shown in gold box, leave blank to hide)" rows={2} value={p.note} onChange={(v) => setP({ ...p, note: v })} />
      {(p.sections || []).map((s, i) => (
        <div key={i} className="border border-[#2B2118]/10 p-4 mb-4">
          <Field label={`Section ${i + 1} — heading`} value={s.heading} onChange={(v) => setSection(i, "heading", v)} />
          <Field label="Body (blank line = new paragraph)" rows={5} value={s.body} onChange={(v) => setSection(i, "body", v)} />
          <button onClick={() => setP({ ...p, sections: p.sections.filter((_, j) => j !== i) })} className="text-xs text-red-400/70">Remove section</button>
        </div>
      ))}
      <div className="flex gap-3 mt-4">
        <SaveBtn onClick={save} testid="policy-save-btn" />
        <button onClick={() => setP({ ...p, sections: [...(p.sections || []), { heading: "", body: "" }] })} className="border border-[#2B2118]/20 px-6 py-2.5 text-xs tracking-[0.2em] uppercase text-[#5A4C3C]">Add section</button>
      </div>
    </div>
  );
};

export const AdminStory = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/story").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const save = async () => { await api.put("/admin/story", { data: s }); toast.success("Saved"); };
  const setPillar = (i, field, v) => { const pillars = [...s.pillars]; pillars[i] = { ...pillars[i], [field]: v }; setS({ ...s, pillars }); };
  return (
    <div className="max-w-3xl">
      <h2 className="font-display text-2xl mb-6">Our Story</h2>
      <Field label="Title" value={s.title} onChange={(v) => setS({ ...s, title: v })} />
      <ImageField label="Story image" value={s.image} onChange={(v) => setS({ ...s, image: v })} />
      <Field label="Intro (blank line = new paragraph)" rows={8} value={s.intro} onChange={(v) => setS({ ...s, intro: v })} />
      {(s.pillars || []).map((pl, i) => (
        <div key={i} className="border border-[#2B2118]/10 p-4 mb-4">
          <Field label={`Pillar 0${i + 1} — title`} value={pl.title} onChange={(v) => setPillar(i, "title", v)} />
          <Field label="Body" rows={3} value={pl.body} onChange={(v) => setPillar(i, "body", v)} />
        </div>
      ))}
      <SaveBtn onClick={save} testid="story-save-btn" />
    </div>
  );
};

export const AdminContact = () => {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/contact").then((r) => setC(r.data)); }, []);
  if (!c) return null;
  const save = async () => { await api.put("/admin/contact", { data: c }); toast.success("Saved"); };
  return (
    <div className="max-w-2xl">
      <h2 className="font-display text-2xl mb-6">Contact Info</h2>
      <Field label="WhatsApp number (digits only, e.g. 923332644998)" value={c.whatsapp} onChange={(v) => setC({ ...c, whatsapp: v })} />
      <Field label="WhatsApp display" value={c.whatsapp_display} onChange={(v) => setC({ ...c, whatsapp_display: v })} />
      <Field label="Email" value={c.email} onChange={(v) => setC({ ...c, email: v })} />
      <Field label="Instagram handle" value={c.instagram} onChange={(v) => setC({ ...c, instagram: v })} />
      <Field label="Region line" value={c.region} onChange={(v) => setC({ ...c, region: v })} />
      <SaveBtn onClick={save} testid="contact-save-btn" />
    </div>
  );
};

export const AdminOrders = () => {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/orders").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    let payload = { status };
    if (status === "dispatched") {
      const courier = window.prompt("Courier name (e.g. TCS, Leopards):", "") || "";
      const tracking = window.prompt("Tracking number:", "") || "";
      payload = { status, courier, tracking };
    }
    const { data } = await api.put(`/admin/orders/${id}`, payload);
    toast.success(status === "dispatched" ? (data.email_sent ? "Dispatched — tracking email sent to customer" : "Dispatched (no customer email on order)") : "Status updated");
    load();
  };
  const colors = { new: "#8F6B3A", confirmed: "#8FB07A", dispatched: "#7A9BB0", delivered: "#5E5B54", cancelled: "#B05A5A" };
  return (
    <div className="max-w-3xl">
      <h2 className="font-display text-2xl mb-6">Orders</h2>
      {list.length === 0 ? (
        <p className="text-sm text-[#6E6151]" data-testid="orders-empty">No orders yet.</p>
      ) : (
        <div className="space-y-4" data-testid="orders-list">
          {list.map((o) => (
            <div key={o.id} className="border border-[#2B2118]/10 p-5" data-testid={`order-${o.order_number}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display text-lg text-[#8F6B3A]">{o.order_number}</p>
                  <p className="text-xs text-[#6E6151]">{(o.created_at || "").slice(0, 16).replace("T", " ")} · PKR {o.total.toLocaleString()} · COD{o.courier || o.tracking ? ` · ${o.courier} ${o.tracking}` : ""}</p>
                </div>
                <select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} data-testid={`order-status-${o.order_number}`}
                  className="bg-[#DDD3C1] border px-3 py-1.5 text-xs uppercase tracking-[0.1em]" style={{ borderColor: colors[o.status], color: colors[o.status] }}>
                  {["new", "confirmed", "dispatched", "delivered", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="mt-3 text-sm text-[#5A4C3C]">
                {o.items.map((i) => <p key={i.slug}>{i.qty} × {i.name} — PKR {(i.price * i.qty).toLocaleString()}</p>)}
              </div>
              <p className="mt-3 text-xs text-[#6E6151]">{o.customer.name} · {o.customer.phone}{o.customer.email ? ` · ${o.customer.email}` : ""} · {o.customer.address}, {o.customer.city}{o.customer.notes ? ` · “${o.customer.notes}”` : ""}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const AdminStoryCards = () => {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/admin/fragrances").then((r) => setList(r.data)); }, []);
  const download = async (slug) => {
    setBusy(slug);
    try {
      const res = await api.get(`/admin/story-card/${slug}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}-story.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Story card downloaded");
    } catch {
      toast.error("Could not generate the story card");
    }
    setBusy("");
  };
  return (
    <div className="max-w-3xl">
      <h2 className="font-display text-2xl mb-2">Instagram Story Cards</h2>
      <p className="text-xs text-[#6E6151] mb-6">Ready-to-post 1080 × 1920 story images with each fragrance's render, notes and price.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="flex items-center gap-4 border border-[#8F6B3A]/30 p-4">
          <p className="flex-1 text-sm text-[#8F6B3A]">The Discovery Set (3 × 5ml)</p>
          <button data-testid="story-card-discovery-set" onClick={() => download("discovery-set")} disabled={busy === "discovery-set"}
            className="border border-[#8F6B3A]/40 text-[#8F6B3A] px-4 py-1.5 text-[10px] tracking-[0.15em] uppercase hover:bg-[#8F6B3A] hover:text-[#FBF8F1] transition-colors disabled:opacity-50">
            {busy === "discovery-set" ? "Rendering…" : "Download"}
          </button>
        </div>
        {list.map((f) => (
          <div key={f.slug} className="flex items-center gap-4 border border-[#2B2118]/10 p-4">
            <img src={f.image} alt="" className="w-16 h-12 object-cover" />
            <p className="flex-1 text-sm">{f.name}</p>
            <button data-testid={`story-card-${f.slug}`} onClick={() => download(f.slug)} disabled={busy === f.slug}
              className="border border-[#8F6B3A]/40 text-[#8F6B3A] px-4 py-1.5 text-[10px] tracking-[0.15em] uppercase hover:bg-[#8F6B3A] hover:text-[#FBF8F1] transition-colors disabled:opacity-50">
              {busy === f.slug ? "Rendering…" : "Download"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AdminReviews = () => {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/reviews").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const approve = async (id) => { await api.put(`/admin/reviews/${id}`); toast.success("Review approved — now live"); load(); };
  const remove = async (id) => { await api.delete(`/admin/reviews/${id}`); toast.success("Review removed"); load(); };
  const pending = list.filter((r) => !r.approved);
  const approved = list.filter((r) => r.approved);
  const Row = ({ r }) => (
    <div className="border border-white/10 p-4" data-testid={`admin-review-${r.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[#C6A87C]">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
        <p className="text-sm">{r.name}</p>
        <p className="text-xs text-[#8A7E6C]">{r.fragrance_slug} · {(r.created_at || "").slice(0, 10)}</p>
        <div className="ml-auto flex gap-3">
          {!r.approved && <button data-testid={`review-approve-${r.id}`} onClick={() => approve(r.id)} className="text-xs text-emerald-500 hover:underline">Approve</button>}
          <button data-testid={`review-delete-${r.id}`} onClick={() => remove(r.id)} className="text-xs text-red-400/80 hover:text-red-400">Delete</button>
        </div>
      </div>
      {r.text && <p className="mt-2 text-sm text-[#6E6151]">{r.text}</p>}
    </div>
  );
  return (
    <div className="max-w-3xl">
      <h2 className="font-display text-2xl mb-6">Reviews</h2>
      <p className="text-[11px] tracking-[0.25em] uppercase text-[#C6A87C] mb-3">Awaiting approval ({pending.length})</p>
      <div className="space-y-3 mb-10">
        {pending.length === 0 ? <p className="text-sm text-[#8A7E6C]">Nothing waiting.</p> : pending.map((r) => <Row key={r.id} r={r} />)}
      </div>
      <p className="text-[11px] tracking-[0.25em] uppercase text-[#8A7E6C] mb-3">Live on site ({approved.length})</p>
      <div className="space-y-3">
        {approved.map((r) => <Row key={r.id} r={r} />)}
      </div>
    </div>
  );
};

export const AdminSignups = () => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/newsletter").then((r) => setList(r.data)); }, []);
  return (
    <div className="max-w-2xl">
      <h2 className="font-display text-2xl mb-2">Newsletter Signups</h2>
      <p className="text-xs text-[#6E6151] mb-6">{list.length} subscriber{list.length === 1 ? "" : "s"}</p>
      {list.length === 0 ? (
        <p className="text-sm text-[#6E6151]" data-testid="signups-empty">No signups yet.</p>
      ) : (
        <div className="divide-y divide-[#2B2118]/10 border border-[#2B2118]/10" data-testid="signups-list">
          {list.map((s) => (
            <div key={s.email} className="flex items-center justify-between p-4">
              <p className="text-sm text-[#2B2118]">{s.email}</p>
              <p className="text-xs text-[#6E6151]">{(s.created_at || "").slice(0, 10)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const AdminHero = () => {
  const [h, setH] = useState(null);
  useEffect(() => { api.get("/hero").then((r) => setH(r.data)); }, []);
  if (!h) return null;
  const save = async () => {
    const body = { ...h, headline_lines: typeof h.headline_lines === "string" ? h.headline_lines.split("\n").filter(Boolean) : h.headline_lines, featured_slugs: typeof h.featured_slugs === "string" ? h.featured_slugs.split(",").map((x) => x.trim()) : h.featured_slugs };
    await api.put("/admin/hero", { data: body }); toast.success("Saved");
  };
  return (
    <div className="max-w-2xl">
      <h2 className="font-display text-2xl mb-6">Home Hero</h2>
      <Field label="Overline" value={h.overline} onChange={(v) => setH({ ...h, overline: v })} />
      <Field label="Headline lines (one per line)" rows={3} value={Array.isArray(h.headline_lines) ? h.headline_lines.join("\n") : h.headline_lines} onChange={(v) => setH({ ...h, headline_lines: v })} />
      <Field label="Subline" rows={2} value={h.subline} onChange={(v) => setH({ ...h, subline: v })} />
      <ImageField label="Hero image" value={h.image} onChange={(v) => setH({ ...h, image: v })} />
      <Field label="Featured slugs (comma separated)" value={Array.isArray(h.featured_slugs) ? h.featured_slugs.join(", ") : h.featured_slugs} onChange={(v) => setH({ ...h, featured_slugs: v })} />
      <SaveBtn onClick={save} testid="hero-save-btn" />
    </div>
  );
};
